import React, { useState, useEffect, useCallback } from 'react';
import styled, { keyframes } from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';

const API = process.env.REACT_APP_API_URL || 'http://localhost:3000/api';

// ─── Styled Components ────────────────────────────────────────────────────────

const Page = styled.div`
  min-height: 100vh;
  background: #0f0f13;
  color: #e5e7eb;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
`;

const TopBar = styled.header`
  background: #18181f;
  border-bottom: 1px solid #2a2a35;
  padding: 0 1.5rem;
  height: 52px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  position: sticky;
  top: 0;
  z-index: 50;
`;

const Brand = styled.div`
  font-size: 0.95rem;
  font-weight: 700;
  color: #a78bfa;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const Badge = styled.span`
  font-size: 0.65rem;
  background: #33415522;
  color: #a78bfa;
  border: 1px solid #33415544;
  padding: 2px 8px;
  border-radius: 99px;
  font-weight: 500;
`;

const Content = styled.div`
  max-width: 1100px;
  margin: 0 auto;
  padding: 1.5rem 1.25rem 4rem;
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
  gap: 0.75rem;
  margin-bottom: 2rem;
`;

const StatCard = styled(motion.div)`
  background: #18181f;
  border: 1px solid #2a2a35;
  border-radius: 12px;
  padding: 1rem 1.25rem;
`;

const StatLabel = styled.div`
  font-size: 0.7rem;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 0.35rem;
`;

const StatValue = styled.div`
  font-size: 1.75rem;
  font-weight: 700;
  color: ${p => p.color || '#e5e7eb'};
  line-height: 1;
`;

const StatSub = styled.div`
  font-size: 0.72rem;
  color: #4b5563;
  margin-top: 0.25rem;
`;

const Toolbar = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 1rem;
  flex-wrap: wrap;
`;

const SearchInput = styled.input`
  flex: 1;
  min-width: 200px;
  background: #18181f;
  border: 1px solid #2a2a35;
  border-radius: 8px;
  padding: 0.5rem 0.75rem;
  color: #e5e7eb;
  font-size: 0.85rem;
  outline: none;
  &::placeholder { color: #4b5563; }
  &:focus { border-color: #334155; }
`;

const Select = styled.select`
  background: #18181f;
  border: 1px solid #2a2a35;
  border-radius: 8px;
  padding: 0.5rem 0.75rem;
  color: #e5e7eb;
  font-size: 0.82rem;
  outline: none;
  cursor: pointer;
  &:focus { border-color: #334155; }
`;

const Btn = styled(motion.button)`
  padding: 0.5rem 1rem;
  border-radius: 8px;
  border: none;
  font-size: 0.82rem;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.4rem;
  background: ${p => p.variant === 'primary' ? '#334155' : p.variant === 'danger' ? '#dc262620' : '#2a2a35'};
  color: ${p => p.variant === 'primary' ? '#fff' : p.variant === 'danger' ? '#f87171' : '#d1d5db'};
  border: ${p => p.variant === 'danger' ? '1px solid #dc262640' : 'none'};
  &:hover { opacity: 0.85; }
`;

const Table = styled.div`
  background: #18181f;
  border: 1px solid #2a2a35;
  border-radius: 12px;
  overflow: hidden;
`;

const TableHead = styled.div`
  display: grid;
  grid-template-columns: 1.3fr 1fr 0.7fr 0.7fr 0.9fr 0.7fr 100px;
  padding: 0.6rem 1rem;
  background: #13131a;
  border-bottom: 1px solid #2a2a35;
  font-size: 0.7rem;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.06em;

  @media (max-width: 700px) {
    grid-template-columns: 1fr 0.6fr 0.9fr 70px;
    & > :nth-child(2), & > :nth-child(4), & > :nth-child(6) { display: none; }
  }
`;

const TableRow = styled(motion.div)`
  display: grid;
  grid-template-columns: 1.3fr 1fr 0.7fr 0.7fr 0.9fr 0.7fr 100px;
  padding: 0.75rem 1rem;
  border-bottom: 1px solid #1e1e28;
  align-items: center;
  font-size: 0.83rem;
  &:last-child { border-bottom: none; }
  &:hover { background: #1c1c25; }

  @media (max-width: 700px) {
    grid-template-columns: 1fr 0.6fr 0.9fr 70px;
    & > :nth-child(2), & > :nth-child(4), & > :nth-child(6) { display: none; }
  }
`;

const StoreName = styled.div`
  font-weight: 600;
  color: #e5e7eb;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const SubText = styled.div`
  color: #6b7280;
  font-size: 0.72rem;
  margin-top: 2px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const PlanBadge = styled.span`
  font-size: 0.7rem;
  font-weight: 600;
  padding: 3px 8px;
  border-radius: 99px;
  background: ${p =>
    p.plan === 'free' ? '#1e3a2f' :
    p.plan === 'pro' ? '#1e2a4a' :
    p.plan === 'scale' ? '#2d1a4a' : '#1e2430'};
  color: ${p =>
    p.plan === 'free' ? '#34d399' :
    p.plan === 'pro' ? '#60a5fa' :
    p.plan === 'scale' ? '#c084fc' : '#94a3b8'};
`;

const StatusDot = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  font-size: 0.75rem;
  color: ${p =>
    p.status === 'ativo' ? '#34d399' :
    p.status === 'inadimplente' ? '#fbbf24' : '#f87171'};
  &::before {
    content: '';
    width: 6px; height: 6px;
    border-radius: 50%;
    background: currentColor;
    flex-shrink: 0;
  }
`;

const ActionMenu = styled.div`
  display: flex;
  gap: 0.35rem;
`;

const IconBtn = styled(motion.button)`
  width: 28px; height: 28px;
  border-radius: 6px;
  border: none;
  background: #2a2a35;
  color: #9ca3af;
  font-size: 0.8rem;
  cursor: pointer;
  display: flex; align-items: center; justify-content: center;
  &:hover { background: #3a3a48; color: #e5e7eb; }
`;

const Pagination = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  margin-top: 1.25rem;
  font-size: 0.82rem;
  color: #6b7280;
`;

// Modal
const Overlay = styled(motion.div)`
  position: fixed; inset: 0;
  background: rgba(0,0,0,0.7);
  backdrop-filter: blur(4px);
  z-index: 200;
  display: flex; align-items: center; justify-content: center;
  padding: 1rem;
`;

const Modal = styled(motion.div)`
  background: #18181f;
  border: 1px solid #2a2a35;
  border-radius: 16px;
  width: 100%;
  max-width: 520px;
  max-height: 90vh;
  overflow-y: auto;
  padding: 1.5rem;
`;

const ModalTitle = styled.h2`
  font-size: 1rem;
  font-weight: 700;
  color: #e5e7eb;
  margin: 0 0 1.25rem;
  display: flex; align-items: center; gap: 0.5rem;
`;

const Field = styled.div`
  margin-bottom: 0.9rem;
  label {
    display: block;
    font-size: 0.75rem;
    color: #9ca3af;
    margin-bottom: 0.3rem;
    font-weight: 500;
  }
  input, select, textarea {
    width: 100%;
    background: #13131a;
    border: 1px solid #2a2a35;
    border-radius: 8px;
    padding: 0.6rem 0.75rem;
    color: #e5e7eb;
    font-size: 0.85rem;
    outline: none;
    box-sizing: border-box;
    &:focus { border-color: #334155; }
    &::placeholder { color: #4b5563; }
  }
`;

const FieldRow = styled.div`display: flex; gap: 0.6rem;`;

const SectionDivider = styled.div`
  font-size: 0.7rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: #4b5563;
  margin: 1.2rem 0 0.6rem;
  padding-top: 0.8rem;
  border-top: 1px solid #2a2a35;
  display: flex; align-items: center; gap: 0.4rem;
`;

const ToggleRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: #13131a;
  border: 1px solid #2a2a35;
  border-radius: 8px;
  padding: 0.6rem 0.75rem;
  margin-bottom: 0.9rem;
`;

const Toggle = styled.button`
  width: 40px; height: 22px;
  border-radius: 11px;
  border: none;
  background: ${p => p.on ? '#334155' : '#2a2a35'};
  position: relative;
  cursor: pointer;
  transition: background 0.2s;
  flex-shrink: 0;
  &::after {
    content: '';
    position: absolute;
    top: 3px;
    left: ${p => p.on ? '21px' : '3px'};
    width: 16px; height: 16px;
    border-radius: 50%;
    background: white;
    transition: left 0.2s;
  }
`;

const ModalFooter = styled.div`
  display: flex;
  gap: 0.75rem;
  justify-content: flex-end;
  margin-top: 1.25rem;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 3rem 1rem;
  color: #4b5563;
  font-size: 0.9rem;
`;

const LoginWrap = styled.div`
  min-height: 100vh;
  background: #0f0f13;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
`;

const LoginCard = styled(motion.div)`
  background: #18181f;
  border: 1px solid #2a2a35;
  border-radius: 16px;
  padding: 2rem;
  width: 100%;
  max-width: 360px;
  text-align: center;
`;

const spin = keyframes`to { transform: rotate(360deg); }`;
const Spinner = styled.div`
  width: 18px; height: 18px;
  border: 2px solid #33415544;
  border-top-color: #334155;
  border-radius: 50%;
  animation: ${spin} 0.7s linear infinite;
  margin: 2rem auto;
`;

// ─── Helpers ──────────────────────────────────────────────────────────────────

const planLabel = { free: 'Free', basico: 'Básico', pro: 'Pro', scale: 'Scale' };
const statusLabel = { ativo: 'Ativo', inadimplente: 'Inadimplente', cancelado: 'Cancelado' };
const fmtDate = d => new Date(d).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: '2-digit' });
const fmtBRL = v => v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

const DIAS = [
  { key: 'seg', label: 'S' }, { key: 'ter', label: 'T' }, { key: 'qua', label: 'Q' },
  { key: 'qui', label: 'Q' }, { key: 'sex', label: 'S' }, { key: 'sab', label: 'S' }, { key: 'dom', label: 'D' },
];

// ─── Component ────────────────────────────────────────────────────────────────

export default function Admin() {
  const params = new URLSearchParams(window.location.search);
  const tokenParam = params.get('token');

  const [token, setToken] = useState(() => tokenParam || localStorage.getItem('admin_token') || '');
  const [tokenInput, setTokenInput] = useState('');
  const [authed, setAuthed] = useState(false);
  const [authError, setAuthError] = useState('');

  const [stats, setStats] = useState(null);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [loading, setLoading] = useState(false);

  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterPlan, setFilterPlan] = useState('');

  const [showModal, setShowModal] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState('');

  const headers = { 'x-admin-token': token, 'Content-Type': 'application/json' };

  const verifyToken = async (t) => {
    try {
      const r = await fetch(`${API}/admin/stats`, { headers: { 'x-admin-token': t } });
      if (r.status === 401) { setAuthError('Token inválido'); return false; }
      setAuthed(true);
      localStorage.setItem('admin_token', t);
      return true;
    } catch {
      setAuthError('Erro ao conectar com o servidor');
      return false;
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setAuthError('');
    const ok = await verifyToken(tokenInput);
    if (ok) setToken(tokenInput);
  };

  const loadStats = useCallback(async () => {
    const r = await fetch(`${API}/admin/stats`, { headers });
    if (r.ok) setStats(await r.json());
  }, [token]); // eslint-disable-line

  const loadList = useCallback(async () => {
    setLoading(true);
    const qs = new URLSearchParams({ page, limit: 20, search, status: filterStatus, plan: filterPlan });
    const r = await fetch(`${API}/admin/studios?${qs}`, { headers });
    if (r.ok) {
      const data = await r.json();
      setRows(data.studios || []);
      setTotal(data.total);
      setPages(data.pages);
    }
    setLoading(false);
  }, [token, page, search, filterStatus, filterPlan]); // eslint-disable-line

  useEffect(() => {
    if (tokenParam) verifyToken(tokenParam);
    else if (token) verifyToken(token);
  }, []); // eslint-disable-line

  useEffect(() => {
    if (authed) { loadStats(); loadList(); }
  }, [authed, loadStats, loadList]);

  useEffect(() => {
    if (!authed) return;
    const t = setTimeout(() => { setPage(1); loadList(); }, 400);
    return () => clearTimeout(t);
  }, [search]); // eslint-disable-line

  useEffect(() => {
    if (authed) { setPage(1); }
  }, [filterStatus, filterPlan]); // eslint-disable-line

  useEffect(() => {
    if (authed) loadList();
  }, [page]); // eslint-disable-line

  const openAdd = () => {
    setEditTarget(null);
    setForm({
      email: '', password: '', phone: '', whatsappNumber: '', storeName: '', ownerName: '',
      plan: 'basico', isFree: false, freeNote: '',
      workStart: '09:00', workEnd: '19:00', workDays: ['seg', 'ter', 'qua', 'qui', 'sex', 'sab'],
      breakTime: 0,
      aiName: 'Assistente', aiTone: 'simpatica', aiActive: true,
    });
    setFormError('');
    setShowModal(true);
  };

  const openEdit = (s) => {
    setEditTarget(s);
    setForm({
      storeName: s.storeName || '',
      ownerName: s.ownerName || '',
      phone: s.phone || '',
      whatsappNumber: s.whatsappNumber || '',
      plan: s.plan || 'basico',
      planStatus: s.planStatus || 'ativo',
      isFree: !!s.isFree,
      freeNote: s.freeNote || '',
      workStart: s.workingHours?.start || '09:00',
      workEnd: s.workingHours?.end || '19:00',
      workDays: s.workingHours?.days || ['seg', 'ter', 'qua', 'qui', 'sex', 'sab'],
      breakTime: s.breakTime || 0,
      aiName: s.aiSettings?.assistantName || 'Assistente',
      aiTone: s.aiSettings?.tone || 'simpatica',
      aiActive: s.aiSettings?.active !== false,
    });
    setFormError('');
    setShowModal(true);
  };

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const toggleDay = (day) => {
    setForm(f => ({
      ...f,
      workDays: f.workDays.includes(day)
        ? f.workDays.filter(d => d !== day)
        : [...f.workDays, day]
    }));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    setFormError('');
    try {
      let r;
      if (editTarget) {
        const body = {
          storeName: form.storeName,
          ownerName: form.ownerName,
          phone: form.phone,
          whatsappNumber: form.whatsappNumber,
          plan: form.isFree ? 'free' : form.plan,
          planStatus: form.planStatus,
          isFree: form.isFree,
          freeNote: form.freeNote,
          workingHours: { start: form.workStart, end: form.workEnd, days: form.workDays },
          breakTime: parseInt(form.breakTime) || 0,
          aiSettings: { assistantName: form.aiName, tone: form.aiTone, active: form.aiActive },
        };
        r = await fetch(`${API}/admin/studios/${editTarget._id}`, {
          method: 'PATCH', headers, body: JSON.stringify(body)
        });
      } else {
        r = await fetch(`${API}/auth/register`, {
          method: 'POST', headers,
          body: JSON.stringify({
            email: form.email, password: form.password,
            storeName: form.storeName, ownerName: form.ownerName, phone: form.phone
          })
        });
        if (r.ok && (form.isFree || form.plan !== 'basico' || form.whatsappNumber)) {
          const created = await r.clone().json();
          await fetch(`${API}/admin/studios/${created.user._id}`, {
            method: 'PATCH', headers,
            body: JSON.stringify({
              plan: form.isFree ? 'free' : form.plan,
              isFree: form.isFree, freeNote: form.freeNote,
              whatsappNumber: form.whatsappNumber,
              workingHours: { start: form.workStart, end: form.workEnd, days: form.workDays },
              breakTime: parseInt(form.breakTime) || 0,
              aiSettings: { assistantName: form.aiName, tone: form.aiTone, active: form.aiActive },
            })
          });
        }
      }
      const data = await r.json();
      if (!r.ok) { setFormError(data.error || 'Erro ao salvar'); setSaving(false); return; }
      setShowModal(false);
      loadStats();
      loadList();
    } catch {
      setFormError('Erro de conexão');
    }
    setSaving(false);
  };

  const handleChangeStatus = async (s, planStatus) => {
    await fetch(`${API}/admin/studios/${s._id}`, {
      method: 'PATCH', headers,
      body: JSON.stringify({ planStatus })
    });
    loadStats(); loadList();
  };

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    setAuthed(false); setToken('');
  };

  // ── Login Screen ────────────────────────────────────────────────────────────
  if (!authed) {
    return (
      <LoginWrap>
        <LoginCard initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          <div style={{ fontSize: '2.5rem', marginBottom: '0.5rem' }}>🔐</div>
          <h1 style={{ fontSize: '1.1rem', fontWeight: 700, color: '#e5e7eb', margin: '0 0 0.25rem' }}>
            Painel Admin
          </h1>
          <p style={{ fontSize: '0.8rem', color: '#6b7280', margin: '0 0 1.5rem' }}>Magzy</p>
          <form onSubmit={handleLogin}>
            <input
              type="password" placeholder="Token de acesso"
              value={tokenInput} onChange={e => setTokenInput(e.target.value)}
              style={{
                width: '100%', background: '#13131a', border: '1px solid #2a2a35',
                borderRadius: '8px', padding: '0.65rem 0.75rem', color: '#e5e7eb',
                fontSize: '0.9rem', outline: 'none', boxSizing: 'border-box', marginBottom: '0.75rem'
              }}
              autoFocus
            />
            {authError && <p style={{ color: '#f87171', fontSize: '0.78rem', margin: '0 0 0.75rem' }}>{authError}</p>}
            <button type="submit" style={{
              width: '100%', padding: '0.7rem', background: '#334155', color: 'white',
              border: 'none', borderRadius: '8px', fontWeight: 700, fontSize: '0.9rem', cursor: 'pointer'
            }}>
              Entrar
            </button>
          </form>
        </LoginCard>
      </LoginWrap>
    );
  }

  // ── Admin Screen ─────────────────────────────────────────────────────────────
  return (
    <Page>
      <TopBar>
        <Brand>
          ✂️ Admin <Badge>Magzy</Badge>
        </Brand>
        <Btn whileTap={{ scale: 0.96 }} onClick={handleLogout} style={{ fontSize: '0.75rem' }}>
          Sair
        </Btn>
      </TopBar>

      <Content>
        {stats ? (
          <StatsGrid>
            <StatCard initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
              <StatLabel>Estúdios</StatLabel>
              <StatValue>{stats.total}</StatValue>
            </StatCard>
            <StatCard initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }}>
              <StatLabel>Ativos</StatLabel>
              <StatValue color="#34d399">{stats.ativos}</StatValue>
            </StatCard>
            <StatCard initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
              <StatLabel>Free / Testers</StatLabel>
              <StatValue color="#c084fc">{stats.free}</StatValue>
            </StatCard>
            <StatCard initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }}>
              <StatLabel>Agendamentos</StatLabel>
              <StatValue color="#60a5fa">{stats.totalAppointments || 0}</StatValue>
            </StatCard>
            <StatCard initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
              <StatLabel>Conversas IA</StatLabel>
              <StatValue color="#fbbf24">{stats.totalConversations || 0}</StatValue>
            </StatCard>
            <StatCard initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.25 }}>
              <StatLabel>Custo IA (mês)</StatLabel>
              <StatValue color="#f59e0b" style={{ fontSize: '1.3rem' }}>{fmtBRL(stats.aiCostMonthBrl || 0)}</StatValue>
              <StatSub>total {fmtBRL(stats.aiCostTotalBrl || 0)}</StatSub>
            </StatCard>
            <StatCard initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}>
              <StatLabel>MRR</StatLabel>
              <StatValue color="#60a5fa" style={{ fontSize: '1.3rem' }}>{fmtBRL(stats.mrr)}</StatValue>
              <StatSub>receita mensal</StatSub>
            </StatCard>
          </StatsGrid>
        ) : <Spinner />}

        <Toolbar>
          <SearchInput
            placeholder="Buscar por nome, telefone, email..."
            value={search} onChange={e => setSearch(e.target.value)}
          />
          <Select value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
            <option value="">Todos os status</option>
            <option value="ativo">Ativo</option>
            <option value="inadimplente">Inadimplente</option>
            <option value="cancelado">Cancelado</option>
          </Select>
          <Select value={filterPlan} onChange={e => setFilterPlan(e.target.value)}>
            <option value="">Todos os planos</option>
            <option value="free">Free</option>
            <option value="basico">Básico</option>
            <option value="pro">Pro</option>
            <option value="scale">Scale</option>
          </Select>
          <Btn variant="primary" whileTap={{ scale: 0.96 }} onClick={openAdd}>
            + Adicionar
          </Btn>
        </Toolbar>

        <Table>
          <TableHead>
            <div>Estúdio</div>
            <div>WhatsApp / Contato</div>
            <div>Plano</div>
            <div>Status</div>
            <div>Custo IA</div>
            <div>Cadastro</div>
            <div>Ações</div>
          </TableHead>

          {loading ? (
            <Spinner />
          ) : rows.length === 0 ? (
            <EmptyState>Nenhum estúdio encontrado</EmptyState>
          ) : (
            <AnimatePresence>
              {rows.map((s, i) => (
                <TableRow
                  key={s._id}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: i * 0.03 }}
                >
                  <div>
                    <StoreName>{s.storeName}</StoreName>
                    <SubText>{s.email}</SubText>
                  </div>
                  <div>
                    <div style={{ color: s.whatsappNumber ? '#34d399' : '#6b7280', fontSize: '0.8rem' }}>
                      {s.whatsappNumber ? `+${s.whatsappNumber}` : '—'}
                    </div>
                    <SubText>{s.phone || '—'}</SubText>
                  </div>
                  <div>
                    <PlanBadge plan={s.plan}>{planLabel[s.plan] || s.plan}</PlanBadge>
                    {s.isFree && <span style={{ fontSize: '0.65rem', color: '#6b7280', marginLeft: 4 }}>🎁</span>}
                  </div>
                  <div>
                    <StatusDot status={s.planStatus}>{statusLabel[s.planStatus]}</StatusDot>
                  </div>
                  <div style={{ lineHeight: 1.45 }}>
                    <div style={{ color: (s.aiCostBrlMonthReal || 0) > 0 ? '#fbbf24' : '#6b7280', fontWeight: 600, fontSize: '0.8rem' }}>
                      {fmtBRL(s.aiCostBrlMonthReal || 0)} <span style={{ fontSize: '0.62rem', color: '#6b7280', fontWeight: 400 }}>real</span>
                    </div>
                    <div style={{ color: (s.aiCostBrlMonthTest || 0) > 0 ? '#a78bfa' : '#6b7280', fontSize: '0.76rem' }}>
                      {fmtBRL(s.aiCostBrlMonthTest || 0)} <span style={{ fontSize: '0.62rem', color: '#6b7280' }}>sim</span>
                    </div>
                    <SubText>total {fmtBRL(s.aiCostBrlTotal || 0)}</SubText>
                  </div>
                  <div style={{ color: '#6b7280', fontSize: '0.78rem' }}>
                    {fmtDate(s.createdAt)}
                  </div>
                  <ActionMenu>
                    <IconBtn title="Editar" whileTap={{ scale: 0.9 }} onClick={() => openEdit(s)}>
                      ✏️
                    </IconBtn>
                    {s.planStatus === 'ativo' ? (
                      <IconBtn title="Inadimplente" whileTap={{ scale: 0.9 }} onClick={() => handleChangeStatus(s, 'inadimplente')} style={{ color: '#fbbf24' }}>
                        ⚠️
                      </IconBtn>
                    ) : (
                      <IconBtn title="Reativar" whileTap={{ scale: 0.9 }} onClick={() => handleChangeStatus(s, 'ativo')} style={{ color: '#34d399' }}>
                        ✅
                      </IconBtn>
                    )}
                    <IconBtn title="Cancelar" whileTap={{ scale: 0.9 }} onClick={() => handleChangeStatus(s, 'cancelado')} style={{ color: '#f87171' }}>
                      🚫
                    </IconBtn>
                  </ActionMenu>
                </TableRow>
              ))}
            </AnimatePresence>
          )}
        </Table>

        {pages > 1 && (
          <Pagination>
            <Btn whileTap={{ scale: 0.96 }} onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}>←</Btn>
            <span>{page} / {pages} — {total} estúdios</span>
            <Btn whileTap={{ scale: 0.96 }} onClick={() => setPage(p => Math.min(pages, p + 1))} disabled={page === pages}>→</Btn>
          </Pagination>
        )}
      </Content>

      {/* Modal add/edit */}
      <AnimatePresence>
        {showModal && (
          <Overlay
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={e => e.target === e.currentTarget && setShowModal(false)}
          >
            <Modal
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
            >
              <ModalTitle>
                {editTarget ? `✏️ ${editTarget.storeName}` : '➕ Novo estúdio'}
              </ModalTitle>

              <form onSubmit={handleSave}>
                {/* Dados básicos */}
                {!editTarget ? (
                  <>
                    <Field>
                      <label>E-mail *</label>
                      <input required type="email" placeholder="dono@email.com" value={form.email} onChange={e => set('email', e.target.value)} />
                    </Field>
                    <Field>
                      <label>Senha provisória *</label>
                      <input required type="password" placeholder="Mínimo 6 caracteres" value={form.password} onChange={e => set('password', e.target.value)} />
                    </Field>
                  </>
                ) : (
                  <Field>
                    <label>E-mail</label>
                    <input value={editTarget.email} disabled style={{ opacity: 0.6 }} />
                  </Field>
                )}

                <Field>
                  <label>Nome do estúdio</label>
                  <input placeholder="Estúdio da Ana" value={form.storeName} onChange={e => set('storeName', e.target.value)} />
                </Field>
                <FieldRow>
                  <Field style={{ flex: 1 }}>
                    <label>Nome do dono</label>
                    <input placeholder="Ana Lima" value={form.ownerName} onChange={e => set('ownerName', e.target.value)} />
                  </Field>
                  <Field style={{ flex: 1 }}>
                    <label>Telefone</label>
                    <input placeholder="5511999998888" value={form.phone} onChange={e => set('phone', e.target.value)} />
                  </Field>
                </FieldRow>

                <Field>
                  <label>Número WhatsApp (para notificações da IA)</label>
                  <input placeholder="5511999998888" value={form.whatsappNumber} onChange={e => set('whatsappNumber', e.target.value)} />
                </Field>

                {/* Assinatura */}
                <SectionDivider>💳 Assinatura</SectionDivider>

                {editTarget && (
                  <Field>
                    <label>Status</label>
                    <select value={form.planStatus} onChange={e => set('planStatus', e.target.value)}>
                      <option value="ativo">Ativo</option>
                      <option value="inadimplente">Inadimplente</option>
                      <option value="cancelado">Cancelado</option>
                    </select>
                  </Field>
                )}

                <ToggleRow>
                  <div>
                    <div style={{ fontSize: '0.83rem', color: '#e5e7eb', fontWeight: 500 }}>🎁 Acesso gratuito</div>
                    <div style={{ fontSize: '0.72rem', color: '#6b7280', marginTop: 2 }}>Beta tester, parceiro</div>
                  </div>
                  <Toggle on={form.isFree} type="button" onClick={() => set('isFree', !form.isFree)} />
                </ToggleRow>

                {form.isFree ? (
                  <Field>
                    <label>Motivo</label>
                    <input placeholder="beta tester, influenciador..." value={form.freeNote} onChange={e => set('freeNote', e.target.value)} />
                  </Field>
                ) : (
                  <Field>
                    <label>Plano</label>
                    <select value={form.plan} onChange={e => set('plan', e.target.value)}>
                      <option value="basico">Básico — R$ 47/mês</option>
                      <option value="pro">Pro — R$ 97/mês</option>
                      <option value="scale">Scale — R$ 197/mês</option>
                    </select>
                  </Field>
                )}

                {/* Horário */}
                <SectionDivider>🕐 Horário de funcionamento</SectionDivider>

                <FieldRow>
                  <Field style={{ flex: 1 }}>
                    <label>Abre às</label>
                    <input type="time" value={form.workStart} onChange={e => set('workStart', e.target.value)} />
                  </Field>
                  <Field style={{ flex: 1 }}>
                    <label>Fecha às</label>
                    <input type="time" value={form.workEnd} onChange={e => set('workEnd', e.target.value)} />
                  </Field>
                  <Field style={{ flex: 1 }}>
                    <label>Intervalo (min)</label>
                    <input type="number" min="0" step="5" value={form.breakTime} onChange={e => set('breakTime', e.target.value)} />
                  </Field>
                </FieldRow>

                <Field>
                  <label>Dias</label>
                  <div style={{ display: 'flex', gap: '0.3rem' }}>
                    {DIAS.map(d => (
                      <button
                        key={d.key} type="button"
                        onClick={() => toggleDay(d.key)}
                        style={{
                          width: 32, height: 32, borderRadius: 6, border: 'none', cursor: 'pointer',
                          fontSize: '0.72rem', fontWeight: 600,
                          background: form.workDays?.includes(d.key) ? '#334155' : '#2a2a35',
                          color: form.workDays?.includes(d.key) ? 'white' : '#6b7280',
                        }}
                      >
                        {d.label}
                      </button>
                    ))}
                  </div>
                </Field>

                {/* IA */}
                <SectionDivider>🤖 Assistente IA</SectionDivider>

                <ToggleRow>
                  <div>
                    <div style={{ fontSize: '0.83rem', color: '#e5e7eb', fontWeight: 500 }}>IA ativa</div>
                    <div style={{ fontSize: '0.72rem', color: '#6b7280', marginTop: 2 }}>Responde automaticamente no WhatsApp</div>
                  </div>
                  <Toggle on={form.aiActive} type="button" onClick={() => set('aiActive', !form.aiActive)} />
                </ToggleRow>

                <FieldRow>
                  <Field style={{ flex: 1 }}>
                    <label>Nome da assistente</label>
                    <input placeholder="Luna, Sofia..." value={form.aiName} onChange={e => set('aiName', e.target.value)} />
                  </Field>
                  <Field style={{ flex: 1 }}>
                    <label>Tom de voz</label>
                    <select value={form.aiTone} onChange={e => set('aiTone', e.target.value)}>
                      <option value="simpatica">Simpática</option>
                      <option value="formal">Formal</option>
                      <option value="animada">Animada</option>
                      <option value="tranquila">Tranquila</option>
                    </select>
                  </Field>
                </FieldRow>

                {formError && (
                  <p style={{ color: '#f87171', fontSize: '0.78rem', margin: '0.75rem 0 0' }}>{formError}</p>
                )}

                <ModalFooter>
                  <Btn type="button" whileTap={{ scale: 0.96 }} onClick={() => setShowModal(false)}>Cancelar</Btn>
                  <Btn type="submit" variant="primary" whileTap={{ scale: 0.96 }} disabled={saving}>
                    {saving ? '...' : editTarget ? 'Salvar' : 'Criar'}
                  </Btn>
                </ModalFooter>
              </form>
            </Modal>
          </Overlay>
        )}
      </AnimatePresence>
    </Page>
  );
}
